import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import ProductApi from "../Api/ProductApi";
import usePermissions from "../hook/usePermissions";
import ImageUploadApi from "../Api/ImageUploadApi";
import { IMAGE_BASE_URL } from "../Config/Index";
import CategoryApi from "../Api/CategoryApi";
import SubCategoryApi from "../Api/SubCategoryApi";
import BrandApi from "../Api/BrandApi";
import UnitApi from "../Api/UnitApi";
import TaxApi from "../Api/TaxApi";
import AttributeApi from "../Api/AttributeApi";
import ImageCropper from "./ImageCropper";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { identifyAverageColor } from "../helper/ColorIdentifier";

const ProductFormLayer = () => {
  const { hasPermission } = usePermissions();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isView = location.pathname.includes("/view/");

  const [formData, setFormData] = useState({
    "name": "",
    "slug": "",
    "categoryId": "",
    "subCategoryId": "",
    "brandId": "",
    "unitId": "",
    "taxId": "",
    "hsnCode": "",
    "weight": "",
    "shortDescription": "",
    "fullDescription": "",
    "refundable": false,
    "status": true,
    "isFeatured": false,
    "isNewArrival": false,
    "lowStockAlert": "",
    "metaTitle": "",
    "metaKeywords": "",
    "metaDescription": "",
    "attributes": [],
    "specifications": [
      {
        "key": "",
        "value": ""
      }
    ]
  });
  const [attributeValueModal, setAttributeValueModal] = useState({ show: false, attributeId: null, name: "", image: null, preview: null, blob: null });
  const [cropperState, setCropperState] = useState({ show: false, image: null });
  const [expandedVariations, setExpandedVariations] = useState({});

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Assuming a generic ReactQuill for text editor
  const [fullDescriptionContent, setFullDescriptionContent] = useState("");
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const booleanOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    fetchBrands();
    fetchUnits();
    fetchTaxes();
    fetchAttributes();
    if (id) fetchDetails();
  }, [id]);

  const fetchCategories = async () => {
    const res = await CategoryApi.getAll(0, 1000, "", true);
    if (res.status && res.response.data) setCategories(res.response.data.map(c => ({ value: c._id, label: c.name })));
  };

  const fetchSubCategories = async () => {
    const res = await SubCategoryApi.getAll(0, 1000, "", true);
    if (res.status && res.response.data) {
      setSubCategories(res.response.data); // Store raw data to access categoryId
    }
  };

  const fetchBrands = async () => {
    const res = await BrandApi.getAll(0, 1000, "");
    if (res.status && res.response.data) setBrands(res.response.data.map(c => ({ value: c._id, label: c.name })));
  };

  const fetchUnits = async () => {
    const res = await UnitApi.getAll(0, 1000, "");
    if (res.status && res.response.data) setUnits(res.response.data.map(c => ({ value: c._id, label: c.name })));
  };

  const fetchTaxes = async () => {
    const res = await TaxApi.getAll(0, 1000, "");
    if (res.status && res.response.data) setTaxes(res.response.data.map(c => ({ value: c._id, label: c.name })));
  };

  const fetchAttributes = async () => {
    const res = await AttributeApi.getAll(0, 1000, "");
    if (res.status && res.response.data) {
      setAttributeOptions(res.response.data.map(attr => ({
        value: attr._id,
        label: attr.name,
        values: attr.values || []
      })));
    }
  };

  const fetchDetails = async () => {
    setLoading(true);
    const response = await ProductApi.getById(id);
    if (response.status) {
      const data = response.response.data || response.response;
      setFormData(prev => ({
        ...prev,
        ...data,
        status: data.status !== undefined ? (data.status === 1 || data.status === "active" || data.status === true) : data.isActive !== undefined ? data.isActive : true
      }));
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "name") {
      const generatedSlug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
      setFormData(prev => ({ ...prev, name: value, slug: generatedSlug }));
      if (errors.name) setErrors(prev => ({ ...prev, name: "" }));
      if (errors.slug) setErrors(prev => ({ ...prev, slug: "" }));
    } else {
      let newValue = type === "checkbox" ? checked : value;
      if (["weight", "lowStockAlert"].includes(name) && value !== "") {
        newValue = Math.max(0, parseFloat(value) || 0);
      }
      setFormData(prev => ({ ...prev, [name]: newValue }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };



  const handleNewAttributeFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setCropperState({ show: true, image: reader.result });
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

   const onNewValueCropComplete = async (cropped) => {
    let colorName = attributeValueModal.name;
    if (!colorName || colorName.trim() === "") {
        const color = await identifyAverageColor(cropped.url);
        colorName = color.name;
    }
    setAttributeValueModal(prev => ({ ...prev, blob: cropped.blob, preview: cropped.url, name: colorName }));
    setCropperState({ show: false, image: null });
  };

  const saveNewAttributeValue = async () => {
    if (!attributeValueModal.name?.trim()) return;

    setLoading(true);
    let finalImage = null;
    if (attributeValueModal.blob) {
      const formDataUpload = new FormData();
      formDataUpload.append("file", attributeValueModal.blob, "swatch.jpg");
      const uploadRes = await ImageUploadApi.uploadImage({ formData: formDataUpload, path: "attributes" });
      if (uploadRes.status) {
        finalImage = uploadRes.response.data || uploadRes.response;
      }
    }

    const attrRes = await AttributeApi.getById(attributeValueModal.attributeId);
    if (attrRes.status) {
      const attrData = attrRes.response.data || attrRes.response;
      const updatedValues = [...(attrData.values || []), { name: attributeValueModal.name, image: finalImage }];

      const updateRes = await AttributeApi.update(attributeValueModal.attributeId, { ...attrData, values: updatedValues });
      if (updateRes.status) {
        await fetchAttributes(); // Refresh options
        setAttributeValueModal({ show: false, attributeId: null, name: "", image: null, preview: null, blob: null });
      }
    }
    setLoading(false);
  };

  const handleVariationImageUpload = async (e, index) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setLoading(true);
      const updated = [...formData.attributes];
      if (!updated[index].images) updated[index].images = [];
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const uploadRes = await ImageUploadApi.uploadImage({ formData: form, path: "products" });
        if (uploadRes.status) {
          updated[index].images.push(uploadRes.response.data || uploadRes.response);
        }
      }
      setFormData(prev => ({ ...prev, attributes: updated }));
      setLoading(false);
    }
  };

  const handleTextEditorChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : (selectedOption === false ? false : "") }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleAddSpecification = () => {
    setFormData(prev => ({ ...prev, specifications: [...prev.specifications, { key: "", value: "" }] }));
  };
  const handleRemoveSpecification = (index) => {
    const updated = [...formData.specifications];
    updated.splice(index, 1);
    setFormData(prev => ({ ...prev, specifications: updated }));
  };
  const handleSpecificationChange = (index, field, value) => {
    const updated = [...formData.specifications];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, specifications: updated }));
  };

  const handleAddAttribute = () => {
    const newIndex = formData.attributes.length;
    setFormData(prev => ({
      ...prev,
      attributes: [...prev.attributes, {
        combination: [{ attributeId: "", value: "", valueId: "" }],
        sku: "",
        stock: "",
        minOrderQty: "",
        maxOrderQty: "",
        mrp: "",
        price: "",
        images: []
      }]
    }));
    // Auto-expand the new one
    setExpandedVariations(prev => ({ ...prev, [newIndex]: true }));
  };

  const toggleVariation = (index) => {
    setExpandedVariations(prev => ({ ...prev, [index]: !prev[index] }));
  };
  const handleRemoveAttribute = (index) => {
    if (window.confirm("Are you sure you want to remove this variation? All its specific data and images will be detached from this list.")) {
      const updated = [...formData.attributes];
      updated.splice(index, 1);
      setFormData(prev => ({ ...prev, attributes: updated }));
    }
  };
  
  const handleCloneAttribute = (index) => {
    const attributeToClone = formData.attributes[index];
    const cloned = JSON.parse(JSON.stringify(attributeToClone));
    // Optionally clear values that should be unique like SKU, but usually users prefer cloning it all
    // cloned.sku = ""; 
    
    setFormData(prev => ({
      ...prev,
      attributes: [...prev.attributes, cloned]
    }));
    
    // Auto-expand the clone
    const newIdx = formData.attributes.length;
    setExpandedVariations(prev => ({ ...prev, [newIdx]: true }));
    toast.success(`Variation #${index + 1} cloned successfully.`);
  };

  const handleAttributeChange = (index, field, value) => {
    const updated = [...formData.attributes];
    let newValue = value;
    if (["mrp", "price", "stock", "minOrderQty", "maxOrderQty"].includes(field) && value !== "") {
      newValue = Math.max(0, parseFloat(value) || 0);
    }
    updated[index][field] = newValue;
    setFormData(prev => ({ ...prev, attributes: updated }));
  };

  const handleAddCombination = (index) => {
    const updated = [...formData.attributes];
    updated[index].combination.push({ attributeId: "", value: "", valueId: "" });
    setFormData(prev => ({ ...prev, attributes: updated }));
  };

  const handleRemoveCombination = (attrIndex, combIndex) => {
    const updated = [...formData.attributes];
    updated[attrIndex].combination.splice(combIndex, 1);
    setFormData(prev => ({ ...prev, attributes: updated }));
  };

  const handleCombinationChange = (attrIndex, combIndex, field, value) => {
    const updated = [...formData.attributes];
    updated[attrIndex].combination[combIndex][field] = value;
    setFormData(prev => ({ ...prev, attributes: updated }));
  };
  const handleAttributeImageUpload = async (e, index) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setLoading(true);
      const updated = [...formData.attributes];
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const uploadRes = await ImageUploadApi.uploadImage({ formData: form, path: "products" });
        if (uploadRes.status) updated[index].images.push(uploadRes.response.data || uploadRes.response);
      }
      setFormData(prev => ({ ...prev, attributes: updated }));
      setLoading(false);
    }
  };
  const handleRemoveAttributeImage = async (attrIndex, imgIndex) => {
    const updated = [...formData.attributes];
    const imgPath = updated[attrIndex].images[imgIndex];
    // We could optionally call the API to delete here
    updated[attrIndex].images.splice(imgIndex, 1);
    setFormData(prev => ({ ...prev, attributes: updated }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = "Product Name is Required.";
    if (!formData.slug?.trim()) newErrors.slug = "Slug is Required.";
    if (!formData.categoryId) newErrors.categoryId = "Category is Required.";
    if (!formData.subCategoryId) newErrors.subCategoryId = "Sub Category is Required.";
    if (!formData.brandId) newErrors.brandId = "Brand is Required.";
    if (!formData.unitId) newErrors.unitId = "Unit is Required.";
    if (!formData.taxId) newErrors.taxId = "Tax is Required.";
    if (formData.refundable === null || formData.refundable === undefined) newErrors.refundable = "Refundable status is Required.";
    if (formData.status === null || formData.status === undefined) newErrors.status = "Status is Required.";
    if (!formData.lowStockAlert) newErrors.lowStockAlert = "Low stock alert is Required.";
    else if (Number(formData.lowStockAlert) < 0) newErrors.lowStockAlert = "Low stock alert cannot be negative.";

    if (formData.weight !== undefined && formData.weight !== null && formData.weight !== "" && Number(formData.weight) < 0) {
      newErrors.weight = "Weight cannot be negative.";
    }

    if (!formData.attributes || formData.attributes.length === 0) {
      newErrors.attributes = "At least one product variation is required.";
      toast.error("Please add at least one product variation.");
    } else {
      const variationsComplete = formData.attributes.every((attr, idx) => {
        const combComplete = attr.combination && 
                           attr.combination.length > 0 && 
                           attr.combination.every(c => c.attributeId && (c.value || c.valueId));
        
        if (!combComplete) {
            toast.error(`Please select attribute name and value for variation #${idx + 1}`);
            return false;
        }
        
        if (!attr.price || parseFloat(attr.price) <= 0) {
            toast.error(`Please enter variation price for variation #${idx + 1}`);
            return false;
        }
        
        if (!attr.stock || parseFloat(attr.stock) < 0) {
            toast.error(`Please enter stock for variation #${idx + 1}`);
            return false;
        }

        if (!attr.images || attr.images.length === 0) {
            toast.error(`Please upload at least one image for variation #${idx + 1}`);
            return false;
        }

        if (!attr.sku) {
            toast.error(`Please enter SKU for variation #${idx + 1}`);
            return false;
        }

        return true;
      });

      if (!variationsComplete) {
        newErrors.attributes = "Please complete all product variation details.";
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0 && !newErrors.attributes) {
      toast.error("Please fill all required fields.");
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    let payload = { ...formData };
    payload.weight = payload.weight ? parseFloat(payload.weight) : null;
    payload.lowStockAlert = payload.lowStockAlert ? parseFloat(payload.lowStockAlert) : null;
    if (payload.attributes && Array.isArray(payload.attributes)) {
      payload.attributes = payload.attributes
        .filter(attr => attr.combination && attr.combination.some(c => c.attributeId)) // Only keep variations with at least one attribute set
        .map(attr => ({
          ...attr,
          combination: attr.combination.filter(c => c.attributeId).map(c => {
            // Find and inject valueId if missing (for older product records)
            if (!c.valueId && c.value && c.attributeId) {
              const selectedAttr = attributeOptions.find(opt => opt.value === c.attributeId);
              const foundVal = selectedAttr?.values?.find(v => v.name === c.value);
              if (foundVal) {
                c.valueId = foundVal._id || foundVal.id;
              }
            }
            return c;
          }),
          sku: attr.sku ? parseFloat(attr.sku) : 0,
          stock: attr.stock ? parseFloat(attr.stock) : 0,
          minOrderQty: attr.minOrderQty ? parseFloat(attr.minOrderQty) : 0,
          maxOrderQty: attr.maxOrderQty ? parseFloat(attr.maxOrderQty) : 0,
          mrp: attr.mrp ? parseFloat(attr.mrp) : 0,
          price: attr.price ? parseFloat(attr.price) : 0,
        }));
    }
    payload.isActive = payload.status;
    let result;
    if (id) result = await ProductApi.update(id, payload);
    else result = await ProductApi.create(payload);
    setLoading(false);
    if (result.status) navigate("/products");
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24">
        <h6 className="text-primary-600 pb-2 mb-0">{isView ? "View" : id ? "Edit" : "Create"} Products</h6>
      </div>
      <div className="card-body p-24">
        <form onSubmit={handleSubmit}>
          <div className="row gy-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Product Name <span className="text-danger">*</span></label>
              <input type="text" className="form-control radius-8" name="name" value={formData.name} onChange={handleChange} placeholder="Enter Product Name" disabled={isView} />
              {errors.name && <small className="text-danger">{errors.name}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Slug <span className="text-danger">*</span></label>
              <input type="text" className="form-control radius-8" name="slug" value={formData.slug} onChange={handleChange} placeholder="Enter Slug" disabled={isView} />
              {errors.slug && <small className="text-danger">{errors.slug}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Category <span className="text-danger">*</span></label>
              <Select name="categoryId" options={categories} value={categories.find((opt) => opt.value === formData.categoryId)} onChange={handleSelectChange} styles={selectStyles(errors.categoryId)} isDisabled={isView} isClearable placeholder="Select Category" />
              {errors.categoryId && <small className="text-danger">{errors.categoryId}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Sub Category <span className="text-danger">*</span></label>
              <Select
                name="subCategoryId"
                options={subCategories.filter(s => s.categoryId === formData.categoryId).map(s => ({ value: s._id, label: s.name }))}
                value={subCategories.filter(s => s.categoryId === formData.categoryId).map(s => ({ value: s._id, label: s.name })).find((opt) => opt.value === formData.subCategoryId)}
                onChange={handleSelectChange}
                styles={selectStyles(errors.subCategoryId)}
                isDisabled={isView || !formData.categoryId}
                isClearable
                placeholder={formData.categoryId ? "Select Sub Category" : "Please select category first"}
              />
              {errors.subCategoryId && <small className="text-danger">{errors.subCategoryId}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Brand <span className="text-danger">*</span></label>
              <Select name="brandId" options={brands} value={brands.find((opt) => opt.value === formData.brandId)} onChange={handleSelectChange} styles={selectStyles(errors.brandId)} isDisabled={isView} isClearable placeholder="Select Brand" />
              {errors.brandId && <small className="text-danger">{errors.brandId}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Unit <span className="text-danger">*</span></label>
              <Select name="unitId" options={units} value={units.find((opt) => opt.value === formData.unitId)} onChange={handleSelectChange} styles={selectStyles(errors.unitId)} isDisabled={isView} isClearable placeholder="Select Unit" />
              {errors.unitId && <small className="text-danger">{errors.unitId}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Tax <span className="text-danger">*</span></label>
              <Select name="taxId" options={taxes} value={taxes.find((opt) => opt.value === formData.taxId)} onChange={handleSelectChange} styles={selectStyles(errors.taxId)} isDisabled={isView} isClearable placeholder="Select Tax" />
              {errors.taxId && <small className="text-danger">{errors.taxId}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">HSN Code</label>
              <input type="text" className="form-control radius-8" name="hsnCode" value={formData.hsnCode} onChange={handleChange} placeholder="Enter HSN Code" disabled={isView} />
              {errors.hsnCode && <small className="text-danger">{errors.hsnCode}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Weight</label>
              <input type="number" className={`form-control radius-8 ${errors.weight ? 'is-invalid' : ''}`} name="weight" value={formData.weight} onChange={handleChange} placeholder="Enter Weight" disabled={isView} min="0" onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()} />
              {errors.weight && <small className="text-danger">{errors.weight}</small>}
            </div>
            <div className="col-md-12">
              <label className="form-label fw-semibold">Short Description</label>
              <textarea className="form-control radius-8" name="shortDescription" value={formData.shortDescription} onChange={handleChange} placeholder="Enter Short Description" disabled={isView} rows="3" />
            </div>
            <div className="col-md-12">
              <label className="form-label fw-semibold">Full Description</label>
              <textarea className="form-control radius-8" name="fullDescription" value={formData.fullDescription} onChange={handleTextEditorChange} placeholder="Enter Full Description (Simulated Text Editor)" disabled={isView} rows="6" />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Refundable (yes/no) <span className="text-danger">*</span></label>
              <Select name="refundable" options={booleanOptions} value={booleanOptions.find((opt) => opt.value === formData.refundable)} onChange={handleSelectChange} styles={selectStyles(errors.refundable)} isDisabled={isView} isClearable={false} />
              {errors.refundable && <small className="text-danger">{errors.refundable}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Status <span className="text-danger">*</span></label>
              <Select name="status" options={statusOptions} value={statusOptions.find((opt) => opt.value === formData.status)} onChange={handleSelectChange} styles={selectStyles(errors.status)} isDisabled={isView} isClearable={false} />
              {errors.status && <small className="text-danger">{errors.status}</small>}
            </div>
            <div className="col-md-6 d-flex align-items-center gap-5 mb-0 mt-32">
              <div className="form-check d-flex align-items-center gap-2">
                <input className="form-check-input" type="checkbox" name="isFeatured" checked={formData.isFeatured || false} onChange={handleChange} id="featuredSwitch" />
                <label className="form-check-label fw-semibold text-secondary-600 mb-0" htmlFor="featuredSwitch">Featured Product</label>
              </div>
              <div className="form-check d-flex align-items-center gap-2">
                <input className="form-check-input" type="checkbox" name="isNewArrival" checked={formData.isNewArrival || false} onChange={handleChange} id="newArrivalSwitch" />
                <label className="form-check-label fw-semibold text-info-600 mb-0" htmlFor="newArrivalSwitch">New Arrival</label>
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Low stock alert qnt <span className="text-danger">*</span></label>
              <input type="number" className={`form-control radius-8 ${errors.lowStockAlert ? 'is-invalid' : ''}`} name="lowStockAlert" value={formData.lowStockAlert} onChange={handleChange} placeholder="Enter Low stock alert qnt" disabled={isView} min="0" onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()} />
              {errors.lowStockAlert && <small className="text-danger">{errors.lowStockAlert}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Meta Title</label>
              <input type="text" className="form-control radius-8" name="metaTitle" value={formData.metaTitle} onChange={handleChange} placeholder="Enter Meta Title" disabled={isView} />
              {errors.metaTitle && <small className="text-danger">{errors.metaTitle}</small>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Meta Keywords</label>
              <input type="text" className="form-control radius-8" name="metaKeywords" value={formData.metaKeywords} onChange={handleChange} placeholder="Enter Meta Keywords" disabled={isView} />
              {errors.metaKeywords && <small className="text-danger">{errors.metaKeywords}</small>}
            </div>
            <div className="col-md-12">
              <label className="form-label fw-semibold">Meta Description</label>
              <textarea className="form-control radius-8" name="metaDescription" value={formData.metaDescription} onChange={handleChange} placeholder="Enter Meta Description" disabled={isView} rows="3" />
            </div>


          </div>
          {/* Product Variations Section */}
          <div className="mt-5 pt-4 border-top">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <h6 className="mb-0 fw-bold text-lg">Product Variations (Combinations)</h6>
              {!isView && (
                <button
                  type="button"
                  className="btn btn-primary-600 radius-8 px-20 py-10 d-flex align-items-center gap-2"
                  onClick={handleAddAttribute}
                  style={{ backgroundColor: "#D81B60", borderColor: "#D81B60" }}
                >
                  <Icon icon="ic:baseline-plus" fontSize="20px" />
                  <span>Add Variation</span>
                </button>
              )}
            </div>

            {formData.attributes.map((attr, index) => {
              const isExpanded = expandedVariations[index];
              const variationSummary = attr.combination?.filter(c => c.value).map(c => c.value).join(" + ") || "New Variation";

              return (
                <div key={index} className={`mb-24 border radius-12 shadow-sm overflow-hidden transition-all ${isExpanded ? "border-primary-200" : "hover-border-primary-100"}`}>
                  {/* Enhanced Collapsible Header */}
                  <div 
                    className={`p-16 d-flex justify-content-between align-items-center cursor-pointer transition-all ${isExpanded ? "bg-primary-50" : "bg-white"}`}
                    onClick={() => toggleVariation(index)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className={`radius-8 p-8 d-flex align-items-center justify-content-center ${isExpanded ? "bg-primary-600 text-white" : "bg-light-200 text-secondary-500"}`}>
                        <Icon 
                          icon={isExpanded ? "ph:caret-up-bold" : "ph:caret-down-bold"} 
                          fontSize="16px"
                        />
                      </div>
                      <div>
                        <h6 className={`text-md mb-0 fw-bold d-flex align-items-center ${isExpanded ? "text-primary-700" : "text-secondary-700"}`}>
                          <Icon icon="ph:package-duotone" className="me-8 text-lg" />
                          Variation #{index + 1} 
                        </h6>
                        <span className="text-secondary-400 fw-medium text-xs mt-2 d-block">{variationSummary}</span>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-4">
                       {/* Preview metrics always shown but styled better */}
                      <div className="d-none d-md-flex gap-12 me-16">
                        <div className="d-flex flex-column align-items-end">
                            <span className="text-xxs text-secondary-400 text-uppercase fw-bold ls-1 mb-1">Price</span>
                            <span className="text-xs fw-bold text-primary-600">₹{attr.price || 0}</span>
                        </div>
                        <div className="vr mx-2 text-secondary-200" style={{ height: "30px" }}></div>
                        <div className="d-flex flex-column align-items-end">
                            <span className="text-xxs text-secondary-400 text-uppercase fw-bold ls-1 mb-1">Stock</span>
                            <span className={`text-xs fw-bold ${attr.stock > 0 ? "text-success-600" : "text-danger-600"}`}>{attr.stock || 0}</span>
                        </div>
                      </div>

                      <div className="d-flex gap-2 ms-12">
                        {!isView && (
                          <>
                            <button
                                type="button"
                                className="btn btn-sm btn-info-focus text-info-600 radius-8 px-12 py-8 d-flex align-items-center gap-2 border-0"
                                onClick={(e) => { e.stopPropagation(); handleCloneAttribute(index); }}
                                title="Duplicate this variation"
                            >
                                <Icon icon="solar:copy-bold-duotone" fontSize="18px" />
                                <span className="fw-bold d-none d-sm-inline">Clone</span>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger-focus text-danger-600 radius-8 px-12 py-8 d-flex align-items-center gap-2 border-0"
                                onClick={(e) => { e.stopPropagation(); handleRemoveAttribute(index); }}
                                title="Delete this variation"
                            >
                                <Icon icon="solar:trash-bin-trash-bold-duotone" fontSize="18px" />
                                <span className="fw-bold d-none d-sm-inline">Remove</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Variation Details - Collapsible Content */}
                  <div className={`p-24 bg-white border-top ${isExpanded ? "d-block animate-fadeIn" : "d-none"}`}>
                    <div className="row gy-4">
                      {/* Nested Combinations section */}
                      <div className="col-12 mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-16 bg-white p-12 radius-8 border">
                          <label className="form-label fw-bold mb-0 text-secondary-700">
                            <Icon icon="material-symbols:settings-input-component-outline" className="me-2" />
                            Attributes Combination (e.g. Red + M)
                          </label>
                          {!isView && (
                            <button
                              type="button"
                              className="btn btn-dark radius-8 px-16 py-8 d-flex align-items-center gap-2"
                              onClick={() => handleAddCombination(index)}
                            >
                              <Icon icon="ic:baseline-plus" width="18" />
                              <span className="text-sm">Add Attribute</span>
                            </button>
                          )}
                        </div>

                        <div className="ps-4 border-start border-3 border-primary-50">
                          {attr.combination && attr.combination.map((comb, cIdx) => {
                            const selectedAttr = attributeOptions.find(opt => opt.value === comb.attributeId);
                            let valueOptions = [];

                            if (selectedAttr?.values) {
                              valueOptions = selectedAttr.values.map(v => ({
                                value: v.name,
                                label: v.name,
                                id: v._id,
                                image: v.image
                              }));
                            }

                            const selectedValue = valueOptions.find(opt => opt.value === comb.value);

                            return (
                              <div key={cIdx} className="row gy-3 mb-16 align-items-end justify-content-start">
                                <div className="col-md-4">
                                  <label className="form-label text-xs fw-bold text-secondary-600 mb-8">Attribute Name <span className="text-danger">*</span></label>
                                  <Select
                                    options={attributeOptions.filter(opt => !attr.combination.some((c, ci) => ci !== cIdx && c.attributeId === opt.value))}
                                    value={attributeOptions.find(opt => opt.value === comb.attributeId)}
                                    onChange={(sel) => handleCombinationChange(index, cIdx, "attributeId", sel ? sel.value : "")}
                                    isDisabled={isView}
                                    styles={selectStyles()}
                                    placeholder="Select Attribute"
                                  />
                                </div>
                                <div className="col-md-4">
                                  <label className="form-label text-xs fw-bold text-secondary-600 mb-8 d-flex justify-content-between align-items-center">
                                    Value
                                    {!isView && comb.attributeId && (
                                      <button type="button" className="btn btn-sm p-0 text-primary-600 d-flex align-items-center gap-1" onClick={() => setAttributeValueModal(prev => ({ ...prev, show: true, attributeId: comb.attributeId }))}>
                                        <Icon icon="ic:baseline-plus" /> Add New
                                      </button>
                                    )}
                                  </label>
                                  <Select
                                    options={valueOptions}
                                    value={valueOptions.find(opt => opt.value === comb.value)}
                                    onChange={(sel) => {
                                      handleCombinationChange(index, cIdx, "value", sel ? sel.value : "");
                                      handleCombinationChange(index, cIdx, "valueId", sel ? sel.id : "");
                                    }}
                                    isDisabled={isView || !comb.attributeId}
                                    styles={selectStyles()}
                                    placeholder="Select Value"
                                  />
                                </div>
                                <div className="col-md-2 d-flex flex-column align-items-center mb-1">
                                  {selectedValue?.image && (
                                    <>
                                      <label className="text-xs fw-bold mb-8 text-secondary-600">Swatch</label>
                                      <div className="border radius-4 p-1 bg-white shadow-sm">
                                        <img src={`${IMAGE_BASE_URL}/${selectedValue.image.path || selectedValue.image}`} alt="Swatch" className="radius-4" style={{ width: "38px", height: "38px", objectFit: "cover" }} />
                                      </div>
                                    </>
                                  )}
                                </div>
                                {!isView && attr.combination.length > 1 && (
                                  <div className="col-md-1 d-flex align-items-center mb-1">
                                    <button
                                      type="button"
                                      className="btn btn-danger radius-8 p-10 d-flex align-items-center justify-content-center"
                                      onClick={() => handleRemoveCombination(index, cIdx)}
                                      style={{ minWidth: "38px", height: "38px" }}
                                    >
                                      <Icon icon="mingcute:delete-2-line" fontSize="18px" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Variation Details */}
                      <div className="col-md-3">
                        <label className="form-label fw-semibold mb-8">Variation MRP</label>
                        <input type="number" className="form-control radius-8 py-10" value={attr.mrp} onChange={(e) => handleAttributeChange(index, "mrp", e.target.value)} disabled={isView} placeholder="MRP" min="0" onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold mb-8">Variation Price <span className="text-danger">*</span></label>
                        <input type="number" className="form-control radius-8 py-10" value={attr.price} onChange={(e) => handleAttributeChange(index, "price", e.target.value)} disabled={isView} placeholder="Price" min="0" onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold mb-8">SKU / Variation Code</label>
                        <input type="text" className="form-control radius-8 py-10" value={attr.sku} onChange={(e) => handleAttributeChange(index, "sku", e.target.value)} disabled={isView} placeholder="SKU" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold mb-8">Stock <span className="text-danger">*</span></label>
                        <input type="number" className="form-control radius-8 py-10" value={attr.stock} onChange={(e) => handleAttributeChange(index, "stock", e.target.value)} disabled={isView} placeholder="Stock" min="0" onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()} />
                      </div>

                      <div className="col-md-12 mt-4">
                        <label className="form-label fw-semibold mb-8">Variation Images (Full Images, Multiple Upload) <span className="text-danger">*</span></label>
                        <div className="p-16 border dashed radius-12 bg-light-50">
                          {!isView && <input type="file" multiple className="form-control radius-8 mb-16" accept="image/*" onChange={(e) => handleVariationImageUpload(e, index)} disabled={loading} />}
                          <div className="d-flex flex-wrap gap-12">
                            {attr.images && attr.images.map((img, i) => (
                              <div key={i} className="position-relative border radius-8 bg-white p-2">
                                <img src={`${IMAGE_BASE_URL}/${img.path || img}`} alt="" className="radius-8" style={{ width: "90px", height: "90px", objectFit: "contain" }} />
                                {!isView && (
                                  <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1 radius-4" onClick={() => handleRemoveAttributeImage(index, i)}>
                                    <Icon icon="fluent:delete-24-regular" style={{ fontSize: "14px" }} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {!isView && formData.attributes.length > 0 && (
              <div className="d-flex justify-content-center mt-0 mb-32">
                <button
                  type="button"
                  className="btn btn-primary-600 radius-8 px-24 py-12 d-flex align-items-center gap-2 shadow-sm"
                  onClick={handleAddAttribute}
                  style={{ backgroundColor: "#D81B60", borderColor: "#D81B60" }}
                >
                  <Icon icon="ic:baseline-plus" fontSize="24px" />
                  <span className="fw-bold">Add Another Variation</span>
                </button>
              </div>
            )}
            {formData.attributes.length === 0 && <div className="text-secondary-light p-32 text-center border dashed radius-12 bg-light-600 fw-medium">No variations added yet. Click "Add Variation" to create product combinations.</div>}
          </div>

          {/* Specifications Section */}
          <div className="mt-5 pt-4 border-top">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <h6 className="mb-0 fw-bold text-lg">Product Specifications</h6>
              {!isView && (
                <button
                  type="button"
                  className="btn btn-primary-600 radius-8 px-20 py-10 d-flex align-items-center gap-2"
                  onClick={handleAddSpecification}
                  style={{ backgroundColor: "#D81B60", borderColor: "#D81B60" }}
                >
                  <Icon icon="ic:baseline-plus" fontSize="20px" />
                  <span>Add Specification</span>
                </button>
              )}
            </div>

            <div className="bg-light-50 p-24 radius-12 border">
              <div className="row gy-4">
                {formData.specifications.map((spec, index) => (
                  <div key={index} className="col-12 d-flex align-items-center gap-3 bg-white p-16 radius-8 shadow-sm">
                    <div className="flex-grow-1">
                      <label className="form-label text-xs fw-bold text-secondary-600 mb-8">Property Name</label>
                      <input type="text" className="form-control radius-8 py-10" value={spec.key} onChange={(e) => handleSpecificationChange(index, "key", e.target.value)} disabled={isView} placeholder="e.g. Style, Material, fit, etc." />
                    </div>
                    <div className="flex-grow-1">
                      <label className="form-label text-xs fw-bold text-secondary-600 mb-8">Value</label>
                      <input type="text" className="form-control radius-8 py-10" value={spec.value} onChange={(e) => handleSpecificationChange(index, "value", e.target.value)} disabled={isView} placeholder="e.g. Regular, Cotton, Slim, etc." />
                    </div>
                    {!isView && (
                      <div className="pt-24">
                        <button
                          type="button"
                          className="btn btn-danger radius-8 p-10 d-flex align-items-center justify-content-center"
                          onClick={() => handleRemoveSpecification(index)}
                          disabled={formData.specifications.length === 1}
                          title="Remove"
                          style={{ minWidth: "42px", height: "42px" }}
                        >
                          <Icon icon="mingcute:delete-2-line" fontSize="20px" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="d-flex justify-content-end gap-3 mt-40 pt-24 border-top">
            <Link to="/products" className="btn btn-outline-secondary px-32 py-12 radius-8 fw-semibold" style={{ minWidth: "140px" }}>
              Cancel
            </Link>
            {!isView && hasPermission("Products", id ? "edit" : "add") && (
              <button
                type="submit"
                className="btn btn-primary radius-8 px-32 py-12 fw-semibold d-flex align-items-center justify-content-center gap-2"
                disabled={loading}
                style={{ backgroundColor: "#364E3D", borderColor: "#364E3D", minWidth: "140px" }}
              >
                {loading ? (
                  <>
                    <Icon icon="line-md:loading-twotone-loop" fontSize="20px" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="tabler:device-floppy" fontSize="20px" />
                    <span>{id ? "Update Product" : "Save Product"}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Add New Attribute Value Modal */}
      <Modal show={attributeValueModal.show} onHide={() => setAttributeValueModal({ ...attributeValueModal, show: false })} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-md">Add New Attribute Value</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-16">
            <label className="form-label fw-semibold">Value Name</label>
            <input type="text" className="form-control radius-8" placeholder="e.g. Cherry Red" value={attributeValueModal.name} onChange={(e) => setAttributeValueModal(prev => ({ ...prev, name: e.target.value }))} />
          </div>
          <div className="mb-16">
            <label className="form-label fw-semibold">Swatch Image (Crop Enabled)</label>
            <input type="file" className="form-control radius-8" accept="image/*" onChange={handleNewAttributeFileChange} />
            {attributeValueModal.preview && (
              <div className="mt-12 text-center border p-2 radius-8 bg-light">
                <img src={attributeValueModal.preview} alt="Swatch" className="radius-8" style={{ width: "80px", height: "80px", objectFit: "cover" }} />
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" className="btn-sm radius-8 px-24" onClick={() => setAttributeValueModal({ ...attributeValueModal, show: false })}>Cancel</Button>
          <Button variant="primary" className="btn-sm radius-8 px-24" disabled={loading} onClick={saveNewAttributeValue}>{loading ? "Saving..." : "Save Value"}</Button>
        </Modal.Footer>
      </Modal>

      {/* Fly-add Cropper */}
      {cropperState.show && (
        <ImageCropper
          image={cropperState.image}
          onCropComplete={onNewValueCropComplete}
          onCancel={() => setCropperState({ show: false, image: null })}
        />
      )}
    </div>
  );
};

export default ProductFormLayer;
